/**
 * Typography — font pairings and per-role specs.
 *
 * Two rules the whole library obeys:
 *   1. Two families per template — a display face and a text face. A pairing
 *      may add a THIRD `script` face only where the script is a brand voice
 *      rather than decoration (a realtor series signing "Just Listed" in
 *      calligraphy), never as a way to add variety.
 *   2. Every family here exists in `lib/fonts.js` EDITOR_FONTS, so the editor's
 *      font dropdown can round-trip it and `loadGoogleFont` can fetch the face.
 *      The quality gate asserts this — an invented font name fails the build.
 *
 * A "role spec" is everything a text element needs except position and copy:
 * family, size, weight, tracking, leading, and case. Templates pull a role and
 * supply only content + geometry, which is what keeps five different layouts
 * looking like one library.
 */

import { TYPE, LEADING, TRACKING } from './tokens';

/**
 * Pairings: a display face that carries personality + a neutral text face that
 * disappears. Both drawn from EDITOR_FONTS.
 */
export const PAIRINGS = {
  'playfair-inter': { display: 'Playfair Display', text: 'Inter', label: 'Playfair Display / Inter' },
  'cormorant-jost': { display: 'Cormorant Garamond', text: 'Jost', label: 'Cormorant Garamond / Jost' },
  'archivo-inter': { display: 'Archivo Black', text: 'Inter', label: 'Archivo Black / Inter' },
  'marcellus-lato': { display: 'Marcellus', text: 'Lato', label: 'Marcellus / Lato' },
  'dmserif-manrope': { display: 'DM Serif Display', text: 'Manrope', label: 'DM Serif Display / Manrope' },
  'elite-signature': {
    display: 'Playfair Display',
    text: 'Lato',
    script: 'Alex Brush',
    label: 'Playfair Display / Lato / Alex Brush',
  },
};

export const getPairing = (id) => {
  const p = PAIRINGS[id];
  if (!p) throw new Error(`templateLibrary: unknown pairing "${id}"`);
  return p;
};

/**
 * Role specs, resolved against a pairing.
 *
 * `upper: true` means the ROLE is set in capitals — templates pass normal-case
 * copy and `applyCase` uppercases it, so the source copy stays readable and
 * editable in the editor as the designer intended it.
 */
export const roles = (pairingId) => {
  const { display, text, script } = getPairing(pairingId);

  return {
    /**
     * Calligraphic brand voice — "Just", "You're Invited", "Dream Home".
     * Script faces carry far more optical white above and below their
     * x-height than a text face, so this role runs at a larger nominal size
     * with tight leading and no tracking; letter-spacing a joined script
     * breaks its connections.
     */
    ...(script
      ? {
          // `isScript` marks these for the quality gate: a script's NOMINAL
          // size is not comparable to a text face's, because most of it is
          // ascender and descender. Left in the hierarchy comparison, a 220px
          // signature would outrank a 215px headline that is visually three
          // times larger.
          script: {
            fontFamily: script, fontSize: TYPE.h1, fontWeight: 400,
            lineHeight: 1.0, letterSpacing: TRACKING.normal, isScript: true,
          },
          scriptSm: {
            fontFamily: script, fontSize: TYPE.h2, fontWeight: 400,
            lineHeight: 1.0, letterSpacing: TRACKING.normal, isScript: true,
          },
        }
      : {}),
    /** Full-bleed statement numerals (a price, a date). */
    hero: {
      fontFamily: display, fontSize: TYPE.hero, fontWeight: 700,
      lineHeight: LEADING.display, letterSpacing: TRACKING.hero,
    },
    /** The one element that carries the page. */
    display: {
      fontFamily: display, fontSize: TYPE.display, fontWeight: 700,
      lineHeight: LEADING.display, letterSpacing: TRACKING.display,
    },
    h1: {
      fontFamily: display, fontSize: TYPE.h1, fontWeight: 700,
      lineHeight: LEADING.heading, letterSpacing: TRACKING.h1,
    },
    h2: {
      fontFamily: display, fontSize: TYPE.h2, fontWeight: 600,
      lineHeight: LEADING.subhead, letterSpacing: TRACKING.h2,
    },
    h3: {
      fontFamily: text, fontSize: TYPE.h3, fontWeight: 600,
      lineHeight: LEADING.subhead, letterSpacing: TRACKING.normal,
    },
    /** Tracked-out uppercase label sitting above a headline. */
    eyebrow: {
      fontFamily: text, fontSize: TYPE.eyebrow, fontWeight: 600,
      lineHeight: LEADING.caption, letterSpacing: TRACKING.eyebrow, upper: true,
    },
    lead: {
      fontFamily: text, fontSize: TYPE.bodyLg, fontWeight: 400,
      lineHeight: LEADING.body, letterSpacing: TRACKING.normal,
    },
    body: {
      fontFamily: text, fontSize: TYPE.body, fontWeight: 400,
      lineHeight: LEADING.body, letterSpacing: TRACKING.normal,
    },
    /** Numbers in a spec row (beds / baths / sqft). */
    stat: {
      fontFamily: display, fontSize: TYPE.h2, fontWeight: 700,
      lineHeight: LEADING.heading, letterSpacing: TRACKING.h2,
    },
    /** The label under a stat. */
    statLabel: {
      fontFamily: text, fontSize: TYPE.caption, fontWeight: 600,
      lineHeight: LEADING.caption, letterSpacing: TRACKING.wide, upper: true,
    },
    cta: {
      fontFamily: text, fontSize: TYPE.body, fontWeight: 700,
      lineHeight: LEADING.caption, letterSpacing: TRACKING.wide, upper: true,
    },
    caption: {
      fontFamily: text, fontSize: TYPE.caption, fontWeight: 400,
      lineHeight: LEADING.caption, letterSpacing: TRACKING.normal,
    },
    micro: {
      fontFamily: text, fontSize: TYPE.micro, fontWeight: 400,
      lineHeight: LEADING.caption, letterSpacing: TRACKING.normal,
    },
  };
};

/** Apply a role's casing rule to copy. */
export const applyCase = (role, copy) => (role.upper ? String(copy).toUpperCase() : String(copy));

/**
 * Per-family LOWERCASE advance as a fraction of font-size — the base the
 * measurement below scales from. Used only by the quality gate to predict
 * overflow; it never affects what renders.
 */
export const WIDTH_FACTOR = {
  // Joined scripts set far narrower than their nominal size suggests.
  'Alex Brush': 0.30,
  'Playfair Display': 0.50,
  'Cormorant Garamond': 0.42,
  'DM Serif Display': 0.48,
  Marcellus: 0.48,
  'Archivo Black': 0.60,
  Inter: 0.50,
  Jost: 0.47,
  Lato: 0.48,
  Manrope: 0.50,
};

/**
 * Advance multipliers per character class, relative to the lowercase base.
 *
 * A single average factor is what let "MICHAEL TAYLOR" pass the gate and then
 * wrap on screen: capitals run roughly 40% wider than lowercase, and a headline
 * set in caps is exactly where the prediction has to be right. Digits sit
 * between the two (most text faces draw them on a tabular width), and spaces
 * are far narrower than either.
 */
const CLASS_ADVANCE = { upper: 1.42, digit: 1.2, lower: 1, space: 0.42, punct: 0.5 };

const classOf = (ch) => {
  if (ch === ' ') return 'space';
  if (ch >= '0' && ch <= '9') return 'digit';
  if (ch >= 'A' && ch <= 'Z') return 'upper';
  if (ch >= 'a' && ch <= 'z') return 'lower';
  return 'punct';
};

/**
 * Largest size at or below `spec.fontSize` at which every line fits `maxWidth`.
 *
 * Blocks that take caller-supplied copy (a feature list, a QR caption, the
 * brand lockup) cannot know in advance whether the longest line fits the box
 * they were given. Rather than have each caller hand-tune a size — and have
 * the gate catch it one template at a time — the block asks for a size that
 * fits. Steps down in whole pixels and never below `min`.
 */
export const fitFontSize = (lines, spec, maxWidth, min = 24) => {
  const widest = (size) => Math.max(
    ...lines.map((l) => measureLine(String(l), { ...spec, fontSize: size })),
  );
  let size = spec.fontSize;
  while (size > min && widest(size) > maxWidth) size -= 1;
  return size;
};

/** Predicted rendered width of one line, in canvas px. */
export const measureLine = (line, { fontFamily, fontSize, fontWeight = 400, letterSpacing = 0 }) => {
  const base = WIDTH_FACTOR[fontFamily] ?? 0.55;
  // Heavier weights are wider; +6% at 700 is a safe approximation.
  const weightAdj = fontWeight >= 700 ? 1.06 : 1;
  const em = fontSize * base * weightAdj;
  let width = 0;
  for (const ch of String(line)) width += em * CLASS_ADVANCE[classOf(ch)] + letterSpacing;
  return width;
};
